import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button, Toolbar, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MedicalServicesOutlined from '@mui/icons-material/MedicalServicesOutlined';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import FixturesService from '../../Services/Fixtures/FixturesService';
import useProminentColor from '../Hooks/useProminentColor';
import ScoreHeader, { getFixtureStatus } from '../Components/ScoreHeader';
import MatchEventsCard, { adaptEvents } from '../Components/MatchEventsCard';
import MatchDetailsCard from '../Components/MatchDetailsCard';
import LineupsCard, { adaptLineup } from '../Components/LineupsCard';
import StatsCard, { adaptStats } from '../Components/StatsCard';
import VenueCard from '../Components/VenueCard';

const fixturesService = new FixturesService();

// ─── Page ─────────────────────────────────────────────────────────────────────

function isValidFixtureId(id) {
  return id != null && /^\d+$/.test(id);
}

export default function FixtureDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const fixtureId = isValidFixtureId(id) ? Number(id) : null;

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(fixtureId !== null);
  const [error, setError] = useState(false);

  const fetchDetails = React.useCallback(() => {
    if (fixtureId === null) return;
    setLoading(true);
    setError(false);
    fixturesService.getFixtureDetails(fixtureId)
      .then(result => {
        setDetails(result ?? null);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [fixtureId]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  const f = details?.fixture;

  // Hooks must be called unconditionally — before any early returns.
  const homeColor = useProminentColor(f?.teams?.home?.badge ?? null);
  const awayColor = useProminentColor(f?.teams?.away?.badge ?? null);
  const colorsReady = homeColor !== null && awayColor !== null;
  const isSingleColumn = useMediaQuery('(max-width: 728px)');

  if (fixtureId === null) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default', gap: 2 }}>
        <SearchOffRounded sx={{ fontSize: 64, color: 'text.disabled' }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            Fixture Not Found
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 260 }}>
            This fixture doesn't exist or the link is invalid.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{ borderRadius: 999, px: 3, textTransform: 'none', fontWeight: 600 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !details || !f) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default', gap: 2 }}>
        <MedicalServicesOutlined sx={{ fontSize: 64, color: 'text.disabled' }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            Call the Physio
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 260 }}>
            We're experiencing some technical issues, please try again later.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshRounded />}
          onClick={fetchDetails}
          sx={{ borderRadius: 999, px: 3, textTransform: 'none', fontWeight: 600 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const { events, lineups, statistics } = details;
  const { status, minute } = getFixtureStatus(f.state);
  const isUpcoming = status === 'TBD' || status === 'NS';

  const fixture = {
    status,
    minute,
    kickoff: isUpcoming && f.date ? new Date(f.date) : null,
    aggregate: f.scores.firstLeg
      ? (!isUpcoming && f.scores.fixture?.home != null && f.scores.fixture?.away != null
          ? { home: f.scores.firstLeg.away + f.scores.fixture.home, away: f.scores.firstLeg.home + f.scores.fixture.away, label: 'agg.' }
          : { home: f.scores.firstLeg.away, away: f.scores.firstLeg.home, label: 'first leg' })
      : null,
    homeTeam: {
      name: f.teams.home.name,
      badgeUrl: f.teams.home.badge,
      score: f.scores.fixture?.home ?? null,
      penaltyScore: f.scores.penalties?.home ?? null,
    },
    awayTeam: {
      name: f.teams.away.name,
      badgeUrl: f.teams.away.badge,
      score: f.scores.fixture?.away ?? null,
      penaltyScore: f.scores.penalties?.away ?? null,
    },
  };

  const adaptedEvents = events ? adaptEvents(events, f.teams.home.id) : [];
  const adaptedHomeLineup = lineups ? adaptLineup(lineups.homeLineup) : null;
  const adaptedAwayLineup = lineups ? adaptLineup(lineups.awayLineup) : null;
  const adaptedStats = statistics?.top ? adaptStats(statistics.top) : [];
  const venue = f.venue ? { name: f.venue.name, city: f.venue.details.city, image: f.venue.image } : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', pb: 4 }}>
      {/* Full-bleed gradient header */}
      <Box
        sx={{
          background: theme => `
            linear-gradient(to bottom, transparent 50%, ${theme.palette.background.default} 100%),
            linear-gradient(to right, ${alpha(homeColor ?? '#000', 0.55)}, ${alpha(awayColor ?? '#000', 0.55)})
          `,
          opacity: colorsReady ? 1 : 0,
          transition: 'opacity 0.4s ease',
          px: { xs: 1, sm: 2 },
        }}
      >
        <Toolbar />
        <Box sx={{ maxWidth: 1216, mx: 'auto', width: '100%', minHeight: 160 }}>
          <ScoreHeader fixture={fixture} />
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ px: { xs: 1, sm: 2 }, mt: 2 }}>
        <Box sx={{ maxWidth: 1216, mx: 'auto', width: '100%' }}>
          {isSingleColumn ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {adaptedEvents.length > 0 && <MatchEventsCard events={adaptedEvents} />}
              {adaptedHomeLineup && adaptedAwayLineup && (
                <LineupsCard fixture={fixture} homeLineup={adaptedHomeLineup} awayLineup={adaptedAwayLineup} homeColor={homeColor} awayColor={awayColor} />
              )}
              {adaptedStats.length > 0 && <StatsCard stats={adaptedStats} homeColor={homeColor} awayColor={awayColor} />}
              {venue && <VenueCard venue={venue} />}
              <MatchDetailsCard competition={f.competition} round={f.round} date={f.date} referee={f.referee} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
              {/* Left column — Match Events + Match Details */}
              <Box sx={{ flex: '1 1 340px', maxWidth: 600, minWidth: 0, alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {adaptedEvents.length > 0 && <MatchEventsCard events={adaptedEvents} />}
                <MatchDetailsCard competition={f.competition} round={f.round} date={f.date} referee={f.referee} />
              </Box>
              {/* Right column — Lineups, Stats, Venue */}
              <Box sx={{ flex: '1 1 340px', maxWidth: 600, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {adaptedHomeLineup && adaptedAwayLineup && (
                  <LineupsCard fixture={fixture} homeLineup={adaptedHomeLineup} awayLineup={adaptedAwayLineup} homeColor={homeColor} awayColor={awayColor} />
                )}
                {adaptedStats.length > 0 && <StatsCard stats={adaptedStats} homeColor={homeColor} awayColor={awayColor} />}
                {venue && <VenueCard venue={venue} />}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
